import * as React from 'react';
import { DetailsList, IColumn, SelectionMode, TooltipHost, IDetailsRowProps, DetailsRow, Selection, IObjectWithKey } from '@fluentui/react';
import { MAShift, MessageStatus } from '../../redux/scheduling/types';
import { LocalDateRange } from './LocalDateRange';
import { TrackingTextField } from '../core/TrackingTextField';
import { HealAPI } from '../../healApi';
import './FloatMAList.scss';
import { css } from '../../util/Css';
import { ToastParams } from '../../redux/message/types';

export interface FloatMessages {
  [maId: string]: MessageStatus;
}

export interface MANotes {
  [maId: string]: string;
}

export interface FloatMAListProps {
  onSelectFloatMA: (MAs: IObjectWithKey[]) => void;
  floatMessages: FloatMessages;
  maNotes: MANotes;
  floatMAShifts: MAShift[];
  shiftType: string;
  actions: {
    toast?: (params: ToastParams) => void;
  };
}

export interface FloatMAListState {
  noteRecentlySaved: {
    [maId: string]: boolean;
  };
}

export class FloatMAList extends React.Component<FloatMAListProps, FloatMAListState> {
  private selection: Selection | undefined;
  private timer: number | undefined;
  public constructor(props: FloatMAListProps) {
    super(props);
    this.selection = new Selection({
      onSelectionChanged: () => {
        if (this.selection) {
          this.props.onSelectFloatMA(this.selection.getSelection());
        }
      },
    });
    this.state = {
      noteRecentlySaved: {},
    };
  }

  /*
   * Lifecycle
   */
  public render(): JSX.Element {
    return (
      <DetailsList
        setKey={'maId'}
        getKey={(item) => item.id}
        columns={this.buildColumns()}
        compact={true}
        onRenderRow={this.onRenderRow}
        items={this.props.floatMAShifts}
        selectionMode={SelectionMode.multiple}
        selection={this.selection}
      />
    );
  }

  private onRenderRow = (props?: IDetailsRowProps): JSX.Element => {
    props = props!;
    const rowClass = css({
      'changes-saved': this.state.noteRecentlySaved[props.item.id],
    });
    return <DetailsRow className={rowClass} {...props} />;
  };

  private setNotesforMA(maId: string, notes: string, date: string, shiftType: string) {
    const trimmedNotes = notes.trim();
    HealAPI.setNotesForFloatMA(maId, trimmedNotes, date, shiftType)
      .then((response) => {
        if (response.ok) {
          const noteRecentlySaved = { ...this.state.noteRecentlySaved };

          noteRecentlySaved[maId] = true;
          this.setState({ noteRecentlySaved });
          this.timer = window.setTimeout(() => {
            const recentlySaved = { ...this.state.noteRecentlySaved };
            delete recentlySaved[maId];
            this.setState({
              noteRecentlySaved: recentlySaved,
            });
          }, 1000);
        }
      })
      .catch(this.displayError);
  }

  /*
   * Helpers
   */

  private displayError = (error: string) => {
    if (this.props.actions.toast) {
      return this.props.actions.toast({ type: 'error', title: 'Error', content: error });
    }
  };

  private formatDate(time: number): string {
    const date = new Date(time * 1000);
    return date.toISOString().substr(0, 10);
  }

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'maConfirmed',
        name: '',
        fieldName: '',
        minWidth: 10,
        maxWidth: 10,
        onRender: (maShift: MAShift) => {
          const messageStatus = this.props.floatMessages[maShift.id];
          if (!messageStatus || messageStatus === MessageStatus.Draft) {
            return null;
          }
          return (
            <TooltipHost content={`Message ${messageStatus}`}>
              <div className={`message-status-badge ${messageStatus}`} />
            </TooltipHost>
          );
        },
      },
      {
        key: 'maName',
        name: 'Assistant',
        fieldName: '',
        minWidth: 90,
        maxWidth: 90,
        onRender: (maShift: MAShift) => {
          const lastInitial = maShift.surname ? maShift.surname[0] : '?';
          return (
            <div>
              <TooltipHost content={`${maShift.name} ${maShift.surname}`}>
                {maShift.name} {lastInitial}.
              </TooltipHost>
            </div>
          );
        },
      },
      {
        key: 'maDateRange',
        name: 'Shift',
        fieldName: '',
        minWidth: 120,
        maxWidth: 120,
        onRender: (maShift: MAShift) => {
          return <LocalDateRange {...maShift.shiftDateRange} />;
        },
      },
      {
        key: 'marketcode',
        name: 'Market',
        fieldName: 'marketCode',
        minWidth: 50,
        maxWidth: 50,
      },
      {
        key: 'notes',
        name: 'Notes',
        fieldName: 'notes',
        minWidth: 170,
        maxWidth: 170,
        onRender: (maShift: MAShift) => {
          const maNotes = this.props.maNotes[maShift.id];
          return (
            <TooltipHost content={maNotes}>
              <TrackingTextField
                borderless={true}
                placeholder={'Enter notes'}
                defaultValue={maNotes}
                multiline={true}
                autoAdjustHeight={true}
                rows={1}
                onFinishedUpdate={(notes) => {
                  this.setNotesforMA(maShift.id, notes, this.formatDate(maShift.shiftDateRange.startTime), this.props.shiftType.toUpperCase());
                }}
              />
            </TooltipHost>
          );
        },
      },
    ];
  }
}
